/**
 * Canonical relative paths for the auth endpoints, plus the classifiers the
 * request/response interceptors use to decide which credential a call carries
 * and how a 401 on it should be recovered.
 *
 * These are the single source of truth — `resources/auth/authEndpoints.ts`
 * builds its URLs from the same constants, so the interceptor's matching can
 * never drift from what the service actually requests.
 */
export const AUTH_PATHS = {
    /** Anonymous: exchange the branch API token for a device token. */
    DEVICE_EXCHANGE: '/auth/device',
    /** Device-token: list the branch roster. */
    DEVICE_USERS: '/auth/device/users',
    /** Device-token: log a seller in by PIN. */
    PIN: '/auth/pin',
    /** Device-token: non-persisting PIN pre-check (shift discrepancy co-sign). */
    PIN_VERIFY: '/auth/pin/verify',
    /** Anonymous: rotate the seller access/refresh pair. */
    REFRESH: '/auth/refresh',
    /** Anonymous: revoke the refresh token (carried in the body) on sign-out. */
    LOGOUT: '/auth/logout',
} as const;

/** Normalise an axios `config.url` to a bare path for matching. */
const toPath = (url: string | undefined): string => {
    if (!url) {
        return '';
    }
    // Strip any query string; endpoints are matched on the path alone.
    const queryIndex = url.indexOf('?');
    return queryIndex === -1 ? url : url.slice(0, queryIndex);
};

/**
 * Endpoints that take the **device** token (`Authorization: Bearer <device>`).
 * A 401 on one of these means the device token is dead → re-enroll.
 */
export const isDeviceTokenEndpoint = (url: string | undefined): boolean => {
    const path = toPath(url);
    return (
        path === AUTH_PATHS.DEVICE_USERS ||
        path === AUTH_PATHS.PIN ||
        path === AUTH_PATHS.PIN_VERIFY
    );
};

/**
 * Endpoints that send **no** token — the device exchange (anonymous), and the
 * refresh and logout calls (they carry the refresh token in the body, not a
 * bearer header).
 * The interceptors never attach a header to these and never try to refresh on a
 * 401 from them (that would loop).
 */
export const isAnonymousAuthEndpoint = (url: string | undefined): boolean => {
    const path = toPath(url);
    return (
        path === AUTH_PATHS.DEVICE_EXCHANGE ||
        path === AUTH_PATHS.REFRESH ||
        path === AUTH_PATHS.LOGOUT
    );
};
